import React, { useEffect, useState, useCallback } from "react";
import NavBar from "@/components/NavBar";
import RideMap from "@/components/RideMap";
import api from "@/lib/api";
import { geocode, reverseGeocode } from "@/lib/geocode";
import { fmt } from "@/lib/format";
import { toast } from "sonner";

const VEHICLES = [
  { id: "standard", label: "STANDARD", desc: "Trajets quotidiens" },
  { id: "premium", label: "PREMIUM", desc: "Véhicules de luxe" },
  { id: "xl", label: "XL", desc: "6 places" },
];

const PAYMENT_LABEL = { cash: "Espèces", card: "Carte", mock: "Démo" };

export default function PassengerDashboard() {
  const [pickup, setPickup] = useState(null);
  const [destination, setDestination] = useState(null);
  const [pickupText, setPickupText] = useState("");
  const [destText, setDestText] = useState("");
  const [vehicle, setVehicle] = useState("standard");
  const [payment, setPayment] = useState("cash");
  const [estimates, setEstimates] = useState(null);
  const [picking, setPicking] = useState("pickup");
  const [activeRide, setActiveRide] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [rating, setRating] = useState({ open: false, val: 5, comment: "" });
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduledFor, setScheduledFor] = useState("");
  const [routes, setRoutes] = useState([]);
  const [routeKind, setRouteKind] = useState("fast");
  const [routesLoading, setRoutesLoading] = useState(false);

  const fetchActive = useCallback(async () => {
    try { const { data } = await api.get("/rides/active"); setActiveRide(data || null); } catch {}
  }, []);
  const fetchDrivers = useCallback(async () => {
    try { const { data } = await api.get("/drivers/online"); setDrivers(data || []); } catch {}
  }, []);

  useEffect(() => {
    fetchActive(); fetchDrivers();
    const t = setInterval(() => { fetchActive(); fetchDrivers(); }, 5000);
    return () => clearInterval(t);
  }, [fetchActive, fetchDrivers]);

  // Fetch OSRM route alternatives when both points are set
  useEffect(() => {
    if (!pickup || !destination || activeRide) { setRoutes([]); return; }
    let cancelled = false;
    (async () => {
      setRoutesLoading(true);
      try {
        const { data } = await api.post("/routes/alternatives", {
          pickup: { lat: pickup.lat, lng: pickup.lng },
          destination: { lat: destination.lat, lng: destination.lng },
        });
        if (!cancelled) {
          setRoutes(data.routes || []);
          if ((data.routes || []).length > 0) setRouteKind(data.routes[0].kind);
        }
      } catch {
        if (!cancelled) setRoutes([]);
      } finally {
        if (!cancelled) setRoutesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pickup, destination, activeRide]);

  const selectedRoute = routes.find(r => r.kind === routeKind) || routes[0];

  const onMapClick = async (latlng) => {
    const addr = await reverseGeocode(latlng.lat, latlng.lng);
    const point = { lat: latlng.lat, lng: latlng.lng, address: addr };
    if (picking === "pickup") { setPickup(point); setPickupText(addr); setPicking("destination"); }
    else { setDestination(point); setDestText(addr); setPicking("pickup"); }
  };

  const searchAddress = async (type) => {
    const q = type === "pickup" ? pickupText : destText;
    if (!q) return;
    const res = await geocode(q);
    if (!res.length) { toast.error("Aucun résultat"); return; }
    const p = res[0];
    if (type === "pickup") { setPickup(p); setPickupText(p.address); }
    else { setDestination(p); setDestText(p.address); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const addr = await reverseGeocode(pos.coords.latitude, pos.coords.longitude);
      setPickup({ lat: pos.coords.latitude, lng: pos.coords.longitude, address: addr });
      setPickupText(addr);
    });
  };

  const estimate = async () => {
    if (!pickup || !destination) { toast.error("Définissez départ et destination"); return; }
    try {
      const payload = { pickup, destination, vehicle_type: vehicle, payment_method: payment };
      if (selectedRoute) payload.route_distance_km = selectedRoute.distance_km;
      const { data } = await api.post("/rides/estimate", payload);
      setEstimates(data);
    } catch { toast.error("Échec de l'estimation"); }
  };

  const book = async () => {
    if (!pickup || !destination) { toast.error("Définissez départ et destination"); return; }
    try {
      const payload = { pickup, destination, vehicle_type: vehicle, payment_method: payment };
      if (selectedRoute) {
        payload.route_distance_km = selectedRoute.distance_km;
        payload.route_choice = selectedRoute.kind;
      }
      if (scheduleOn && scheduledFor) payload.scheduled_for = new Date(scheduledFor).toISOString();
      const { data } = await api.post("/rides/book", payload);
      toast.success(data.status === "scheduled" ? "Course planifiée !" : "Course demandée ! En attente d'un chauffeur");
      setActiveRide(data); setEstimates(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de la réservation"); }
  };

  const cancelRide = async () => {
    if (!activeRide) return;
    try { await api.post(`/rides/${activeRide.ride_id}/cancel`); setActiveRide(null); toast.success("Course annulée"); }
    catch { toast.error("Échec de l'annulation"); }
  };

  const submitRating = async () => {
    try {
      await api.post(`/rides/${activeRide.ride_id}/rate`, { rating: rating.val, comment: rating.comment });
      toast.success("Merci pour votre avis");
      setRating({ open: false, val: 5, comment: "" });
      setActiveRide(null);
    } catch { toast.error("Échec de la notation"); }
  };

  const reschedule = async (newIso) => {
    try {
      const { data } = await api.patch(`/rides/${activeRide.ride_id}/reschedule`, { scheduled_for: new Date(newIso).toISOString() });
      setActiveRide(data);
      toast.success("Heure modifiée");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la modification");
      return false;
    }
  };

  const payNow = async () => {
    try {
      const { data } = await api.post("/payments/checkout", { ride_id: activeRide.ride_id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch { toast.error("Échec de l'initialisation du paiement"); }
  };

  const mapCenter = pickup ? [pickup.lat, pickup.lng] : [46.2044, 6.1432]; // Genève par défaut

  return (
    <div className="min-h-screen bg-white">
      <NavBar><span className="font-mono-eyebrow hidden md:inline">// Passager</span></NavBar>

      <div className="grid lg:grid-cols-12 gap-0 min-h-[calc(100vh-64px)]">
        <aside className="lg:col-span-4 border-r-2 border-black p-6 bg-[#F4F4F5] overflow-y-auto" data-testid="passenger-panel">
          <p className="font-mono-eyebrow">// Réserver</p>
          <h1 className="font-display text-3xl mt-1">OÙ ALLEZ-VOUS ?</h1>

          {activeRide ? (
            <ActiveRidePanel ride={activeRide} onCancel={cancelRide} onPay={payNow} onRate={() => setRating(r => ({ ...r, open: true }))} onReschedule={reschedule} />
          ) : (
            <>
              <div className="mt-6 space-y-3">
                <div>
                  <label className="font-mono-eyebrow flex justify-between items-center">
                    <span>Départ</span>
                    <button data-testid="use-my-location-btn" onClick={useMyLocation} className="text-[10px] underline">Ma position</button>
                  </label>
                  <div className="flex gap-2 mt-2">
                    <input data-testid="pickup-input" value={pickupText} onChange={(e) => setPickupText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchAddress("pickup"))} placeholder="Adresse de départ" className="brut-input flex-1" />
                    <button data-testid="pickup-search-btn" onClick={() => searchAddress("pickup")} className="brut-btn px-3">→</button>
                  </div>
                </div>
                <div>
                  <label className="font-mono-eyebrow">Destination</label>
                  <div className="flex gap-2 mt-2">
                    <input data-testid="destination-input" value={destText} onChange={(e) => setDestText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchAddress("destination"))} placeholder="Adresse de destination" className="brut-input flex-1" />
                    <button data-testid="destination-search-btn" onClick={() => searchAddress("destination")} className="brut-btn px-3">→</button>
                  </div>
                </div>
                <p className="text-xs text-[#52525B]">Astuce : cliquez sur la carte pour définir {picking === "pickup" ? "le départ" : "la destination"}.</p>
              </div>

              <p className="font-mono-eyebrow mt-6">// Itinéraire</p>
              {routesLoading && <p className="text-xs mt-1 text-[#52525B]">Calcul des trajets...</p>}
              {!routesLoading && routes.length === 0 && pickup && destination && (
                <p className="text-xs mt-1 text-[#52525B]">Trajet non calculable (point hors zone routière ?)</p>
              )}
              {routes.length > 0 && (
                <div className="mt-2 space-y-2" data-testid="routes-list">
                  {routes.map((r) => {
                    const isSel = r.kind === routeKind;
                    const label = r.kind === "fast" ? "RAPIDE" : "COURT";
                    return (
                      <button
                        key={r.kind}
                        data-testid={`route-${r.kind}`}
                        onClick={() => setRouteKind(r.kind)}
                        className={`w-full border-2 border-black p-3 text-left flex justify-between items-center ${isSel ? "bg-[#FFD600]" : "bg-white"}`}
                      >
                        <div>
                          <div className="font-display text-base flex items-center gap-2">
                            <span className={`w-3 h-3 inline-block border-2 border-black`} style={{background: r.kind === "fast" ? "#FFD600" : "#3B82F6"}} />
                            {label}
                          </div>
                          <div className="text-xs font-mono-eyebrow text-[#52525B] mt-1">{r.distance_km}KM · {Math.round(r.duration_min)}MIN</div>
                        </div>
                        <div className="text-right font-display text-base">{fmt(r.fare)}</div>
                      </button>
                    );
                  })}
                </div>
              )}

              <p className="font-mono-eyebrow mt-6">// Choisir un véhicule</p>
              <div className="mt-3 space-y-2">
                {VEHICLES.map(v => (
                  <button key={v.id} data-testid={`vehicle-${v.id}`} onClick={() => setVehicle(v.id)} className={`w-full border-2 border-black p-3 text-left flex justify-between items-center ${vehicle === v.id ? "bg-[#FFD600]" : "bg-white"}`}>
                    <div>
                      <div className="font-display text-lg">{v.label}</div>
                      <div className="text-xs text-[#52525B]">{v.desc}</div>
                    </div>
                    {estimates?.estimates?.[v.id] && (
                      <div className="text-right">
                        <div className="font-display text-lg">{fmt(estimates.estimates[v.id].fare)}</div>
                        <div className="text-[10px] font-mono-eyebrow">{estimates.estimates[v.id].eta_minutes}MIN</div>
                      </div>
                    )}
                  </button>
                ))}
              </div>

              <p className="font-mono-eyebrow mt-6">// Paiement</p>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {["cash", "card", "mock"].map(p => (
                  <button key={p} data-testid={`payment-${p}`} onClick={() => setPayment(p)} className={`border-2 border-black py-2 font-mono-eyebrow uppercase ${payment === p ? "bg-black text-[#FFD600]" : "bg-white"}`}>{PAYMENT_LABEL[p]}</button>
                ))}
              </div>

              <p className="font-mono-eyebrow mt-6">// Réservation à l'avance</p>
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  data-testid="schedule-toggle-btn"
                  onClick={() => setScheduleOn(s => !s)}
                  className={`border-2 border-black px-3 py-2 font-mono-eyebrow uppercase ${scheduleOn ? "bg-[#FFD600]" : "bg-white"}`}
                >{scheduleOn ? "PLANIFIÉE" : "IMMÉDIATE"}</button>
                {scheduleOn && (
                  <input
                    data-testid="schedule-datetime-input"
                    type="datetime-local"
                    value={scheduledFor}
                    min={new Date(Date.now() + 10*60*1000).toISOString().slice(0,16)}
                    onChange={(e) => setScheduledFor(e.target.value)}
                    className="brut-input flex-1"
                  />
                )}
              </div>
              {scheduleOn && (
                <p className="text-[10px] text-[#52525B] mt-1">Au moins 10 minutes dans le futur. Visible par les chauffeurs immédiatement.</p>
              )}

              <div className="mt-6 flex gap-2">
                <button data-testid="estimate-btn" onClick={estimate} className="brut-btn brut-btn-secondary flex-1">Estimer</button>
                <button data-testid="book-ride-btn" onClick={book} className="brut-btn flex-1">Réserver →</button>
              </div>
              {estimates && (
                <div className="mt-4 p-3 border-2 border-black bg-white">
                  <p className="font-mono-eyebrow">// Estimation</p>
                  <p className="font-display text-2xl">{fmt(estimates.estimates[vehicle].fare)}</p>
                  <p className="text-xs">Distance : {estimates.distance_km}km · ETA : {estimates.estimates[vehicle].eta_minutes}min</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    <span className="brut-badge">{estimates.estimates[vehicle].tariff}</span>
                    <span className="brut-badge">CHF {estimates.estimates[vehicle].per_km}/km</span>
                    <span className="brut-badge">Prise: CHF {estimates.estimates[vehicle].base_fare}</span>
                    {estimates.estimates[vehicle].gva_pickup && <span className="brut-badge" style={{background:"#0A0A0A",color:"#FFD600"}}>GVA</span>}
                    {estimates.estimates[vehicle].out_of_canton && <span className="brut-badge" style={{background:"#0A0A0A",color:"#FFD600"}}>HORS CANTON</span>}
                  </div>
                  <p className="text-[10px] text-[#52525B] mt-2">Tarif Taxi Genève · T1 lun-ven 06h31–20h30 · sam 06h31–18h30 · T2 sinon · Dim/jours fériés = T2 · Zone : Genève + Vaud · Attente 1.00 CHF/min</p>
                </div>
              )}
            </>
          )}
        </aside>
        <main className="lg:col-span-8 p-4">
          <RideMap
            center={mapCenter}
            pickup={pickup}
            destination={destination}
            drivers={activeRide ? [] : drivers}
            onMapClick={onMapClick}
            routes={routes}
            selectedRouteKind={routeKind}
            route={(routes.length === 0 && pickup && destination && !activeRide) ? [[pickup.lat, pickup.lng], [destination.lat, destination.lng]] : null}
            height="calc(100vh - 120px)"
          />
        </main>
      </div>

      {rating.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="brut-card p-6 w-full max-w-md bg-white" data-testid="rating-modal">
            <p className="font-mono-eyebrow">// Notez votre chauffeur</p>
            <h2 className="font-display text-3xl mt-2">VOTRE AVIS ?</h2>
            <div className="flex gap-2 mt-4">
              {[1,2,3,4,5].map(n => (
                <button key={n} data-testid={`rate-${n}`} onClick={() => setRating(r => ({...r, val: n}))} className={`w-12 h-12 border-2 border-black font-display text-xl ${rating.val >= n ? "bg-[#FFD600]" : "bg-white"}`}>★</button>
              ))}
            </div>
            <textarea data-testid="rating-comment" value={rating.comment} onChange={e => setRating(r => ({...r, comment: e.target.value}))} className="brut-input mt-3" rows={3} placeholder="Commentaire (optionnel)..." />
            <button data-testid="submit-rating-btn" onClick={submitRating} className="brut-btn w-full mt-3">Envoyer →</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ActiveRidePanel({ ride, onCancel, onPay, onRate, onReschedule }) {
  const [editing, setEditing] = useState(false);
  const [newDt, setNewDt] = useState("");

  const submitReschedule = async () => {
    if (!newDt) return;
    const ok = await onReschedule(newDt);
    if (ok) { setEditing(false); setNewDt(""); }
  };

  const statusMap = {
    pending: { txt: "RECHERCHE DE CHAUFFEUR", color: "bg-[#FFD600]" },
    scheduled: { txt: "PLANIFIÉE", color: "bg-[#FFD600]" },
    accepted: { txt: "CHAUFFEUR EN ROUTE", color: "bg-black text-[#FFD600]" },
    in_progress: { txt: "EN COURSE", color: "bg-black text-[#FFD600]" },
    completed: { txt: "TERMINÉE", color: "bg-[#00C853] text-white" },
  };
  const s = statusMap[ride.status] || { txt: ride.status, color: "bg-white" };

  return (
    <div className="mt-6" data-testid="active-ride-panel">
      <div className={`border-2 border-black p-3 ${s.color}`}>
        <p className="font-mono-eyebrow">// Statut</p>
        <p className="font-display text-2xl">{s.txt}</p>
      </div>
      <div className="mt-4 brut-card p-4">
        {ride.scheduled_for && (
          <>
            <div className="flex justify-between items-center">
              <div>
                <p className="font-mono-eyebrow">Planifiée pour</p>
                <p className="font-display text-lg">{new Date(ride.scheduled_for).toLocaleString("fr-CH")}</p>
              </div>
              {["scheduled", "accepted"].includes(ride.status) && (
                <button data-testid="edit-schedule-btn" onClick={() => setEditing(e => !e)} className="brut-btn brut-btn-secondary text-xs px-3 py-1">{editing ? "Annuler" : "Modifier"}</button>
              )}
            </div>
            {editing && (
              <div className="mt-3 flex gap-2" data-testid="reschedule-form">
                <input
                  data-testid="reschedule-datetime-input"
                  type="datetime-local"
                  value={newDt}
                  min={new Date(Date.now() + 10*60*1000).toISOString().slice(0,16)}
                  onChange={e => setNewDt(e.target.value)}
                  className="brut-input flex-1"
                />
                <button data-testid="submit-reschedule-btn" onClick={submitReschedule} className="brut-btn px-3">OK</button>
              </div>
            )}
            <div className="border-t-2 border-black my-3" />
          </>
        )}
        <p className="font-mono-eyebrow">Départ</p>
        <p className="text-sm">{ride.pickup?.address}</p>
        <p className="font-mono-eyebrow mt-3">Destination</p>
        <p className="text-sm">{ride.destination?.address}</p>
        <div className="flex justify-between mt-3 pt-3 border-t-2 border-black">
          <span className="font-mono-eyebrow">{ride.vehicle_type?.toUpperCase()}</span>
          <span className="font-display text-xl">{fmt(ride.fare)}</span>
        </div>
        {ride.driver_name && (
          <div className="mt-3 pt-3 border-t-2 border-black">
            <p className="font-mono-eyebrow">Chauffeur</p>
            <p className="font-display text-lg">{ride.driver_name}</p>
            <p className="text-xs">{ride.driver_vehicle} · {ride.driver_plate}</p>
          </div>
        )}
      </div>

      <div className="mt-4 space-y-2">
        {ride.status === "completed" && ride.payment_method === "card" && ride.payment_status !== "paid" && (
          <button data-testid="pay-now-btn" onClick={onPay} className="brut-btn w-full">PAYER {fmt(ride.fare)} →</button>
        )}
        {ride.status === "completed" && (
          <button data-testid="rate-driver-btn" onClick={onRate} className="brut-btn brut-btn-secondary w-full">Noter le chauffeur</button>
        )}
        {["pending", "scheduled", "accepted"].includes(ride.status) && (
          <button data-testid="cancel-ride-btn" onClick={onCancel} className="brut-btn brut-btn-secondary w-full" style={{background:"#FF3B30",color:"white"}}>Annuler la course</button>
        )}
      </div>
    </div>
  );
}
